// Forum Discussion Types 
export type DiscussionSortBy = 'oldest' | 'newest' | 'most_liked';

// Reaction Actions
export type ReactionAction = 'like' | 'unlike' | 'dislike' | 'undislike';

// Standard API Response wrapper
export interface APIResponse<T> {
  success: boolean;
  code: number;
  description: string;
  data: T;
  meta_data?: {
    generated_at?: string;
    tenant_key?: string;
    thread_id?: string;
    filters_applied?: Record<string, any>;
  };
  error?: string;
}

// Discussion User Information (populated field)
export interface DiscussionUser {
  _id: string;
  given_name: string;
  family_name: string;
  profile_picture?: string;
}

// Forum Discussion Response DTO (matches ForumDiscussionResponseDto from backend)
export interface ForumDiscussionDto {
  _id: string;                          // Unique discussion identifier
  user_id: string;                      // Creator user ID
  message: string;                      // Discussion message (plain text)
  message_html?: string;                // Discussion message (HTML)
  parent_id: string | null;             // Parent discussion ID (null for top-level)
  reply_to_user_id?: string;            // User being replied to
  reply_depth: number;                  // Nesting depth (0 for top-level)
  is_deleted: boolean;                  // Soft delete flag
  is_edited: boolean;                   // Whether message was edited
  like_count: number;                   // Number of likes
  dislike_count: number;                // Number of dislikes
  reply_count: number;                  // Number of direct replies
  mentioned_users: string[];            // Array of mentioned user IDs
  is_flagged: boolean;                  // Whether discussion is flagged
  is_approved: boolean;                 // Whether discussion is approved
  createdAt: Date;                      // Creation timestamp
  updatedAt: Date;                      // Last update timestamp

  // Optional populated fields
  user?: DiscussionUser;                // Populated user information
  replies?: ForumDiscussionDto[];       // Nested replies
  user_liked?: boolean;                 // If current user liked
  user_disliked?: boolean;              // If current user disliked
}

// Discussion List Response DTO (matches backend flat structure)
export interface ForumDiscussionListResponseDto {
  discussions: ForumDiscussionDto[];
  total: number;           // Total number of discussions
  page: number;            // Current page number
  limit: number;           // Items per page
  total_pages?: number;    // Total number of pages (optional)
}

// Create Discussion DTO (matches CreateDiscussionDto from backend)
export interface CreateDiscussionDto {
  thread_id: string;                    // Required: associated thread
  message: string;                      // Required: discussion message
  message_html?: string;                // Optional: HTML version of message
  parent_id?: string | null;            // Optional: parent discussion (for replies)
  reply_to_user_id?: string;            // Optional: user being replied to
  mentioned_users?: string[];           // Optional: mentioned user IDs
}

// Update Discussion DTO (matches UpdateDiscussionDto from backend)
export interface UpdateDiscussionDto {
  thread_id: string;                    // Required: thread ID
  discussion_id: string;                // Required: discussion to update
  message: string;                      // Required: updated message
  message_html?: string;                // Optional: updated HTML message
  mentioned_users?: string[];           // Optional: updated mentions
}

// Get Discussions Query Parameters (matches GetDiscussionsDto from backend)
export interface GetDiscussionsDto {
  thread_id: string;                    
  parent_id?: string | null;            
  page?: number;                        
  limit?: number;                       
  sort_by?: DiscussionSortBy;    
}

// Delete Discussion DTO (matches DeleteDiscussionDto from backend)
export interface DeleteDiscussionDto {
  thread_id: string;                    // Required: thread ID
  discussion_id: string;                // Required: discussion to delete
}

// React to Discussion DTO (matches LikeDiscussionDto from backend)
export interface LikeDiscussionDto {
  thread_id: string;                    // Required: thread ID
  discussion_id: string;                // Required: discussion to react to
  action: ReactionAction;               // Required: like/unlike/dislike/undislike
}

// API Response Types
export type CreateDiscussionResponse = APIResponse<ForumDiscussionDto>;
export type GetDiscussionResponse = APIResponse<ForumDiscussionDto | null>;
export type UpdateDiscussionResponse = APIResponse<ForumDiscussionDto>;
export type DeleteDiscussionResponse = APIResponse<{ deleted: boolean; discussion_id: string }>;
export type DiscussionListResponse = APIResponse<ForumDiscussionListResponseDto>;
export type ReactDiscussionResponse = APIResponse<ForumDiscussionDto>;

// Form Types (for React Hook Form)
export interface DiscussionFormData {
  message: string;
  mentioned_users?: string[];
}

// Helper function to get user display name
export function getDiscussionAuthorName(discussion: ForumDiscussionDto): string {
  if (discussion.user) {
    return `${discussion.user.given_name} ${discussion.user.family_name}`.trim();
  }
  return 'Unknown User';
}

// Helper function to get author initials for avatar
export function getDiscussionAuthorInitials(discussion: ForumDiscussionDto): string {
  if (discussion.user) {
    const firstName = discussion.user.given_name || '';
    const lastName = discussion.user.family_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  return 'U';
}

// Helper function to format discussion date
export function formatDiscussionDate(date: Date | string | undefined): string {
  // Handle undefined or null dates
  if (!date) {
    return 'Unknown';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Unknown';
  }

  const now = new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInSeconds = diffInMs / 1000;
  const diffInMinutes = diffInSeconds / 60;
  const diffInHours = diffInMinutes / 60;
  const diffInDays = diffInHours / 24;

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    const minutes = Math.floor(diffInMinutes);
    return `${minutes}m ago`;
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours}h ago`;
  } else if (diffInDays < 7) {
    const days = Math.floor(diffInDays);
    return `${days}d ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks}w ago`;
  } else {
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
}

// Helper function to extract mentions from message
export function extractMentions(message: string): string[] {
  const mentionPattern = /@(\w+)/g;
  const mentions: string[] = [];
  let match;
  while ((match = mentionPattern.exec(message)) !== null) {
    mentions.push(match[1]);
  }
  return mentions;
}

// Helper function to highlight mentions in message
export function highlightMentions(message: string): string {
  return message.replace(/@(\w+)/g, '<span class="text-indigo-600 font-medium">@$1</span>');
}

// Helper function to calculate net likes (like_count - dislike_count)
export function getNetLikes(discussion: ForumDiscussionDto): number {
  return discussion.like_count - discussion.dislike_count;
}
